/**
 * DecisionFlow — BonusPoolManager
 * ui/src/components/tenant-admin/BonusPoolManager.jsx
 *
 * Domain: §P4-09 Bonus Pools
 * Nav route: /admin/bonus-pools
 * API:
 *   GET  /api/v1/bonus-pools?fiscal_period_id={id}
 *   POST /api/v1/bonus-pools
 *   POST /api/v1/bonus-pools/{id}/approve  (NN-10)
 *   GET  /api/v1/bonus-pool-allocations?bonus_pool_id={id}
 *   POST /api/v1/bonus-pool-allocations
 *   POST /api/v1/bonus-pool-allocations/{id}/approve  (NN-10)
 *   GET  /api/v1/bonus-pool-results?calculation_run_id={id}  (read-only)
 * Feature gate: Enterprise — bonus_pool
 *   403 FEATURE_UNAVAILABLE → GateStatusBanner.
 * Tabs: Pools | Allocations | Results (read-only)
 * Pool must be approved before allocations can be created.
 * allocation_source always 'manager_nomination' from UI.
 * Bonus pool allocation state: is_approved boolean (not derived from approved_by).
 * Standard state for pools: derived from approved_by / submitted_at.
 * Path: ui/src/components/tenant-admin/BonusPoolManager.jsx
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import ApprovalPanel from '../shared/ApprovalPanel.jsx';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function derivePoolState(entity) {
  if (entity.approved_by)                         return 'approved';
  if (entity.submitted_at || entity.submitted_by) return 'pending_approval';
  return 'draft';
}

// Allocation state: is_approved boolean
function allocationState(alloc) {
  return alloc.is_approved ? 'approved' : 'draft';
}

function getApiErrorCode(error) { return error?.code || null; }
function getErrorMessage(error, fallback) { return error?.message || fallback; }

// ---------------------------------------------------------------------------
// CSS tokens
// ---------------------------------------------------------------------------

const thClass      = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';
const tdClass      = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
const btnPrimary   = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';
const inputClass   = 'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';
const labelClass   = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'pools',       label: 'Pools'       },
  { key: 'allocations', label: 'Allocations' },
  { key: 'results',     label: 'Results'     },
];

// ---------------------------------------------------------------------------
// Form defaults
// ---------------------------------------------------------------------------

const EMPTY_POOL = {
  pool_name:       '',
  total_budget:    '',
  currency:        'USD',
  effective_date:  '',
  expiration_date: '',
};

const EMPTY_ALLOC = {
  position_id:    '',
  alloc_amount:   '',
  notes:          '',
};

// ---------------------------------------------------------------------------
// BonusPoolManager
// ---------------------------------------------------------------------------

export default function BonusPoolManager({
  mode, period, wizardSteps, embedded = false,
  planId, planComponentId, fiscalPeriodId, readOnly = false,
}) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.bonus_pool')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }
  const fpId = period?.fiscal_period_id ?? fiscalPeriodId ?? null;

  const [gated, setGated]           = useState(false);
  const [activeTab, setActiveTab]   = useState('pools');

  // ── Pools ──
  const [pools, setPools]           = useState([]);
  const [poolsLoading, setPoolsLoading] = useState(false);
  const [poolsError, setPoolsError] = useState('');
  const [selectedPool, setSelectedPool] = useState(null);
  const [showPoolForm, setShowPoolForm] = useState(false);
  const [poolForm, setPoolForm]     = useState(EMPTY_POOL);
  const [poolSaving, setPoolSaving] = useState(false);
  const [poolFormError, setPoolFormError] = useState('');
  const [poolActionLoading, setPoolActionLoading] = useState(false);
  const [poolActionError, setPoolActionError]     = useState('');

  // ── Allocations ──
  const [allocs, setAllocs]         = useState([]);
  const [allocsLoading, setAllocsLoading] = useState(false);
  const [allocsError, setAllocsError]     = useState('');
  const [selectedAlloc, setSelectedAlloc] = useState(null);
  const [showAllocForm, setShowAllocForm] = useState(false);
  const [allocForm, setAllocForm]   = useState(EMPTY_ALLOC);
  const [allocSaving, setAllocSaving]     = useState(false);
  const [allocFormError, setAllocFormError] = useState('');
  const [allocActionLoading, setAllocActionLoading] = useState(false);
  const [allocActionError, setAllocActionError]     = useState('');

  // ── Results ──
  const [results, setResults]       = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError]     = useState('');

  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg) => { setToast(msg); window.setTimeout(() => setToast(null), 3000); }, []);

  // ---------------------------------------------------------------------------
  // Load pools — enterprise gate first
  // ---------------------------------------------------------------------------

  const loadPools = useCallback(async () => {
    if (!user) return;
    setPoolsLoading(true); setPoolsError(''); setGated(false);
    try {
      const url = fpId
        ? `/api/v1/bonus-pools?fiscal_period_id=${fpId}`
        : '/api/v1/bonus-pools';
      const data = await apiGet(url);
      setPools(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'FEATURE_UNAVAILABLE') { setGated(true); }
      else { setPoolsError(getErrorMessage(error, 'Unable to load bonus pools.')); }
    } finally { setPoolsLoading(false); }
  }, [user, fpId]);

  useEffect(() => { loadPools(); }, [loadPools]);

  // ---------------------------------------------------------------------------
  // Load allocations for selected pool
  // ---------------------------------------------------------------------------

  const loadAllocs = useCallback(async (poolId) => {
    if (!poolId) return;
    setAllocsLoading(true); setAllocsError('');
    try {
      const data = await apiGet(`/api/v1/bonus-pool-allocations?bonus_pool_id=${poolId}`);
      setAllocs(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (e) { setAllocsError(getErrorMessage(e, 'Unable to load allocations.')); }
    finally { setAllocsLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'allocations' && selectedPool) loadAllocs(selectedPool.bonus_pool_id);
  }, [activeTab, selectedPool, loadAllocs]);

  // ---------------------------------------------------------------------------
  // Load results
  // ---------------------------------------------------------------------------

  const loadResults = useCallback(async () => {
    if (!selectedPool) return;
    setResultsLoading(true); setResultsError('');
    try {
      const data = await apiGet(`/api/v1/bonus-pool-results?bonus_pool_id=${selectedPool.bonus_pool_id}`);
      setResults(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (e) { setResultsError(getErrorMessage(e, 'Unable to load results.')); }
    finally { setResultsLoading(false); }
  }, [selectedPool]);

  useEffect(() => { if (activeTab === 'results') loadResults(); }, [activeTab, loadResults]);

  // ---------------------------------------------------------------------------
  // Pool actions
  // ---------------------------------------------------------------------------

  async function handleCreatePool(e) {
    e.preventDefault();
    if (!poolForm.pool_name.trim() || !poolForm.total_budget) { setPoolFormError('Pool name and budget are required.'); return; }
    setPoolSaving(true); setPoolFormError('');
    try {
      await apiPost('/api/v1/bonus-pools', {
        fiscal_period_id: fpId,
        pool_name:        poolForm.pool_name.trim(),
        total_budget:     parseFloat(poolForm.total_budget),
        currency:         poolForm.currency || 'USD',
        effective_date:   poolForm.effective_date || null,
        expiration_date:  poolForm.expiration_date || null,
      });
      setShowPoolForm(false); setPoolForm(EMPTY_POOL);
      showToast('Bonus pool created.');
      await loadPools();
    } catch (e) { setPoolFormError(getErrorMessage(e, 'Failed to create pool.')); }
    finally { setPoolSaving(false); }
  }

  async function handlePoolSubmit() {
    if (!selectedPool) return;
    setPoolActionLoading(true); setPoolActionError('');
    try {
      await apiPost(`/api/v1/bonus-pools/${selectedPool.bonus_pool_id}/submit`);
      showToast('Submitted for approval.');
      await loadPools();
      setSelectedPool((p) => ({ ...p, submitted_at: new Date().toISOString() }));
    } catch (e) { setPoolActionError(getErrorMessage(e, 'Failed to submit.')); }
    finally { setPoolActionLoading(false); }
  }

  async function handlePoolApprove() {
    if (!selectedPool) return;
    setPoolActionLoading(true); setPoolActionError('');
    try {
      await apiPost(`/api/v1/bonus-pools/${selectedPool.bonus_pool_id}/approve`);
      showToast('Bonus pool approved.');
      await loadPools();
      setSelectedPool((p) => ({ ...p, approved_by: user?.user_id || user?.id }));
    } catch (e) {
      const code = getApiErrorCode(e);
      setPoolActionError(code === 'SELF_APPROVAL_NOT_ALLOWED' ? 'Self-approval is not permitted (NN-10).' : getErrorMessage(e, 'Failed to approve.'));
    } finally { setPoolActionLoading(false); }
  }

  // ---------------------------------------------------------------------------
  // Allocation actions
  // ---------------------------------------------------------------------------

  async function handleCreateAlloc(e) {
    e.preventDefault();
    if (!selectedPool) { setAllocFormError('Select an approved pool first.'); return; }
    if (derivePoolState(selectedPool) !== 'approved') {
      setAllocFormError('Pool must be approved before allocations can be created.');
      return;
    }
    if (!allocForm.position_id.trim() || !allocForm.alloc_amount) { setAllocFormError('Position ID and allocation amount are required.'); return; }
    setAllocSaving(true); setAllocFormError('');
    try {
      await apiPost('/api/v1/bonus-pool-allocations', {
        bonus_pool_id:     selectedPool.bonus_pool_id,
        position_id:       allocForm.position_id.trim(),
        alloc_amount:      parseFloat(allocForm.alloc_amount),
        allocation_source: 'manager_nomination',
        notes:             allocForm.notes.trim() || null,
      });
      setShowAllocForm(false); setAllocForm(EMPTY_ALLOC);
      showToast('Allocation created.');
      await loadAllocs(selectedPool.bonus_pool_id);
    } catch (e) { setAllocFormError(getErrorMessage(e, 'Failed to create allocation.')); }
    finally { setAllocSaving(false); }
  }

  async function handleAllocApprove() {
    if (!selectedAlloc) return;
    setAllocActionLoading(true); setAllocActionError('');
    try {
      await apiPost(`/api/v1/bonus-pool-allocations/${selectedAlloc.allocation_id}/approve`);
      showToast('Allocation approved.');
      await loadAllocs(selectedPool.bonus_pool_id);
      setSelectedAlloc((a) => ({ ...a, is_approved: true }));
    } catch (e) {
      const code = getApiErrorCode(e);
      setAllocActionError(code === 'SELF_APPROVAL_NOT_ALLOWED' ? 'Self-approval is not permitted (NN-10).' : getErrorMessage(e, 'Failed to approve.'));
    } finally { setAllocActionLoading(false); }
  }

  // ---------------------------------------------------------------------------
  // Guards
  // ---------------------------------------------------------------------------

  const currentUserId = user?.user_id || user?.id || null;

  if (!user) return (
    <section className="flex flex-col gap-4">
      <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">Authentication required.</div>
    </section>
  );

  if (gated) return (
    <section className="flex flex-col gap-4">
      <GateStatusBanner feature="bonus_pool" />
    </section>
  );

  function NoPoolSelected() {
    return (
      <div className="rounded-lg border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
        Select a bonus pool in the Pools tab first.
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-tok-lg font-bold text-[rgb(var(--color-text))]">Bonus Pools</h1>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-[rgb(var(--color-border))]">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-tok-sm font-medium border-b-2 transition ${
              activeTab === t.key
                ? 'border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]'
                : 'border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Pools tab ── */}
      {activeTab === 'pools' && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Bonus Pools</h2>
            {!readOnly && <button type="button" onClick={() => { setShowPoolForm((v) => !v); setPoolFormError(''); setPoolForm(EMPTY_POOL); }} className={btnPrimary}>{showPoolForm ? 'Cancel' : '+ New Pool'}</button>}
          </div>

          {showPoolForm && (
            <form onSubmit={handleCreatePool} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">New Bonus Pool</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className={labelClass + ' sm:col-span-2'}>Pool Name <span className="text-[rgb(var(--color-danger))]">*</span><input type="text" value={poolForm.pool_name} onChange={(e) => setPoolForm((f) => ({ ...f, pool_name: e.target.value }))} placeholder="e.g. Q2 Sales Excellence Pool" className={inputClass} required /></label>
                <label className={labelClass}>Total Budget <span className="text-[rgb(var(--color-danger))]">*</span><input type="number" step="0.01" min="0" value={poolForm.total_budget} onChange={(e) => setPoolForm((f) => ({ ...f, total_budget: e.target.value }))} placeholder="0.00" className={inputClass} required /></label>
                <label className={labelClass}>Currency<input type="text" maxLength={3} value={poolForm.currency} onChange={(e) => setPoolForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))} placeholder="USD" className={inputClass} /></label>
                <label className={labelClass}>Effective Date<input type="date" value={poolForm.effective_date} onChange={(e) => setPoolForm((f) => ({ ...f, effective_date: e.target.value }))} className={inputClass} /></label>
                <label className={labelClass}>Expiration Date<input type="date" value={poolForm.expiration_date} onChange={(e) => setPoolForm((f) => ({ ...f, expiration_date: e.target.value }))} className={inputClass} /></label>
              </div>
              {poolFormError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{poolFormError}</div>}
              <div className="flex gap-2"><button type="submit" disabled={poolSaving} className={btnPrimary}>{poolSaving ? 'Saving…' : 'Create Pool'}</button><button type="button" onClick={() => { setShowPoolForm(false); setPoolFormError(''); }} className={btnSecondary}>Cancel</button></div>
            </form>
          )}

          {poolsLoading && <div className="animate-pulse space-y-2"><div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" /><div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" /></div>}
          {poolsError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{poolsError}</div>}
          {!poolsLoading && !poolsError && pools.length === 0 && (
            <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No bonus pools</p>
              <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">Create a pool to allocate discretionary bonus budgets for the period.</p>
            </div>
          )}
          {pools.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Pool Name</th>
                    <th className={thClass}>Budget</th>
                    <th className={thClass}>Currency</th>
                    <th className={thClass}>Effective</th>
                    <th className={thClass}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pools.map((p) => (
                    <tr key={p.bonus_pool_id}
                      className={`cursor-pointer hover:bg-[rgb(var(--color-surface-muted))] transition-colors ${selectedPool?.bonus_pool_id === p.bonus_pool_id ? 'bg-[rgb(var(--color-primary))]/5' : ''}`}
                      onClick={() => { setSelectedPool(p); setPoolActionError(''); }}
                    >
                      <td className={tdClass + ' font-medium'}>{p.pool_name}</td>
                      <td className={tdClass}>{p.total_budget != null ? Number(p.total_budget).toLocaleString() : '—'}</td>
                      <td className={tdClass}>{p.currency ?? '—'}</td>
                      <td className={tdClass}>{p.effective_date ?? '—'}</td>
                      <td className={tdClass}><StatusPill status={derivePoolState(p)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {selectedPool && !readOnly && (() => {
            const state = derivePoolState(selectedPool);
            return (
              <div className="flex flex-col gap-3">
                <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
                  Selected: <strong className="text-[rgb(var(--color-text))]">{selectedPool.pool_name}</strong>
                  {state !== 'approved' && (
                    <span className="ml-2 text-tok-xs text-[rgb(var(--color-warning))]">— Pool must be approved before allocations can be created.</span>
                  )}
                </div>
                {poolActionError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{poolActionError}</div>}
                <ApprovalPanel variant="bonus_pool" currentState={state} submittedBy={selectedPool.submitted_by} currentUserId={currentUserId}
                  onSubmit={state === 'draft' ? handlePoolSubmit : null} onApprove={state === 'pending_approval' ? handlePoolApprove : null} onReject={null}
                  approvedBy={selectedPool.approved_by} approvedAt={selectedPool.approved_at} loading={poolActionLoading} />
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Allocations tab ── */}
      {activeTab === 'allocations' && (
        <div className="flex flex-col gap-4">
          {!selectedPool ? <NoPoolSelected /> : (
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Allocations — {selectedPool.pool_name}</h2>
                  {derivePoolState(selectedPool) !== 'approved' && (
                    <p className="text-tok-xs text-[rgb(var(--color-warning))]">Pool must be approved before allocations can be added.</p>
                  )}
                </div>
                {!readOnly && derivePoolState(selectedPool) === 'approved' && (
                  <button type="button" onClick={() => { setShowAllocForm((v) => !v); setAllocFormError(''); setAllocForm(EMPTY_ALLOC); }} className={btnPrimary}>{showAllocForm ? 'Cancel' : '+ Allocate'}</button>
                )}
              </div>

              {showAllocForm && (
                <form onSubmit={handleCreateAlloc} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
                  <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">New Allocation (Manager Nomination)</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className={labelClass}>Position ID <span className="text-[rgb(var(--color-danger))]">*</span><input type="text" value={allocForm.position_id} onChange={(e) => setAllocForm((f) => ({ ...f, position_id: e.target.value }))} placeholder="e.g. POS-0042" className={inputClass} required /></label>
                    <label className={labelClass}>Amount <span className="text-[rgb(var(--color-danger))]">*</span><input type="number" step="0.01" min="0" value={allocForm.alloc_amount} onChange={(e) => setAllocForm((f) => ({ ...f, alloc_amount: e.target.value }))} placeholder="0.00" className={inputClass} required /></label>
                    <label className={labelClass + ' sm:col-span-2'}>Notes<textarea value={allocForm.notes} onChange={(e) => setAllocForm((f) => ({ ...f, notes: e.target.value }))} rows={2} className={inputClass} /></label>
                  </div>
                  {allocFormError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{allocFormError}</div>}
                  <div className="flex gap-2"><button type="submit" disabled={allocSaving} className={btnPrimary}>{allocSaving ? 'Saving…' : 'Create Allocation'}</button><button type="button" onClick={() => { setShowAllocForm(false); setAllocFormError(''); }} className={btnSecondary}>Cancel</button></div>
                </form>
              )}

              {allocsLoading && <div className="animate-pulse space-y-2"><div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" /></div>}
              {allocsError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{allocsError}</div>}
              {!allocsLoading && !allocsError && allocs.length === 0 && (
                <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
                  <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No allocations. Approve the pool, then allocate to positions.</p>
                </div>
              )}
              {allocs.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr>
                        <th className={thClass}>Position</th>
                        <th className={thClass}>Amount</th>
                        <th className={thClass}>Source</th>
                        <th className={thClass}>Approved</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allocs.map((a) => (
                        <tr key={a.allocation_id}
                          className={`cursor-pointer hover:bg-[rgb(var(--color-surface-muted))] transition-colors ${selectedAlloc?.allocation_id === a.allocation_id ? 'bg-[rgb(var(--color-primary))]/5' : ''}`}
                          onClick={() => { setSelectedAlloc(a); setAllocActionError(''); }}
                        >
                          <td className={tdClass + ' font-medium'}>{a.position_id}</td>
                          <td className={tdClass}>{a.alloc_amount != null ? Number(a.alloc_amount).toLocaleString() : '—'}</td>
                          <td className={tdClass}>{a.allocation_source ?? 'manager_nomination'}</td>
                          <td className={tdClass}>
                            <StatusPill status={allocationState(a)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedAlloc && !readOnly && !selectedAlloc.is_approved && (
                <div className="flex flex-col gap-3">
                  <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
                    Selected allocation: Position <strong className="text-[rgb(var(--color-text))]">{selectedAlloc.position_id}</strong>
                  </div>
                  {allocActionError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{allocActionError}</div>}
                  <div className="flex gap-2">
                    <button type="button" onClick={handleAllocApprove} disabled={allocActionLoading} className={btnPrimary}>
                      {allocActionLoading ? 'Approving…' : 'Approve Allocation'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Results tab (read-only) ── */}
      {activeTab === 'results' && (
        <div className="flex flex-col gap-4">
          <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Results are engine-written and read-only. Run a calculation to populate.
          </div>
          {!selectedPool && <NoPoolSelected />}
          {selectedPool && (
            <>
              {resultsLoading && <div className="animate-pulse space-y-2"><div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" /></div>}
              {resultsError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{resultsError}</div>}
              {!resultsLoading && !resultsError && results.length === 0 && (
                <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
                  <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No results for this pool. Run a calculation to generate results.</p>
                </div>
              )}
              {results.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr>
                        <th className={thClass}>Position</th>
                        <th className={thClass}>Allocated</th>
                        <th className={thClass}>Paid</th>
                        <th className={thClass}>Variance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((r, i) => (
                        <tr key={r.result_id ?? i} className="hover:bg-[rgb(var(--color-surface-muted))]">
                          <td className={tdClass}>{r.position_id ?? '—'}</td>
                          <td className={tdClass}>{r.allocated_amount != null ? Number(r.allocated_amount).toLocaleString() : '—'}</td>
                          <td className={tdClass}>{r.paid_amount != null ? Number(r.paid_amount).toLocaleString() : '—'}</td>
                          <td className={tdClass}>{r.variance != null ? Number(r.variance).toLocaleString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {toast && (
        <div role="status" className="fixed bottom-4 right-4 z-50 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text))] shadow-lg">{toast}</div>
      )}
    </section>
  );
}
